import { useState } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Copy, BookOpen } from "lucide-react";

function CodeBlock({ code }: { code: string }) {
  const { toast } = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast({ title: "Copied to clipboard" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };
  return (
    <div className="relative group">
      <pre className="overflow-x-auto rounded-md bg-muted p-4 text-xs font-mono leading-relaxed border">
        <code>{code}</code>
      </pre>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="absolute top-2 right-2 h-7 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={copy}
      >
        <Copy className="h-3 w-3 mr-1" /> Copy
      </Button>
    </div>
  );
}

interface Field {
  name: string;
  type: string;
  required: "yes" | "no";
  notes?: string;
}

const createFields: Field[] = [
  { name: "customerName", type: "string", required: "yes" },
  { name: "mobileNumber", type: "string", required: "yes", notes: "E.164 or local format" },
  { name: "source", type: "enum", required: "yes", notes: "organic_website · meta_ads · google_ads · ads_website · blog · walk_in · referral · call · other" },
  { name: "spaceType", type: "enum", required: "yes", notes: "private_cabin · day_pass · five_days_pass · seven_days_pass · workstation · meeting_room · podcast_room · conference_room" },
  { name: "seatRangeMin", type: "integer", required: "yes" },
  { name: "seatRangeMax", type: "integer", required: "yes" },
  { name: "urgency", type: "enum", required: "yes", notes: "low · medium · high · immediate" },
  { name: "location", type: "string", required: "yes", notes: "Customer's preferred area" },
  { name: "spaceId", type: "integer", required: "yes", notes: "The hub the lead is interested in" },
  { name: "numberVerified", type: "boolean", required: "no", notes: "Default false" },
  { name: "stage", type: "enum", required: "no", notes: "Default not_contacted" },
  { name: "callStatus", type: "enum", required: "no", notes: "Default not_started" },
  { name: "leadStatus", type: "string", required: "no", notes: "Default new" },
  { name: "campaignId", type: "string", required: "no", notes: "Free-form campaign reference" },
  { name: "nextFollowUpDate", type: "ISO date-time", required: "no" },
  { name: "callbackScheduledDate", type: "ISO date-time", required: "no" },
  { name: "callbackScheduledSlot", type: "string", required: "no" },
  { name: "followUpCount", type: "integer", required: "no", notes: "Default 0" },
  { name: "externalId", type: "string", required: "no", notes: "Stable ID from your system. Enables idempotent upserts on PATCH." },
  { name: "assignedToWhatsappNumber", type: "string", required: "no", notes: "WhatsApp number of the agent/admin to assign. Resolved server-side." },
];

export default function ApiDocs() {
  const baseUrl = typeof window !== "undefined"
    ? `${window.location.protocol}//${window.location.host}/api`
    : "https://your-host/api";
  const [host, setHost] = useState(baseUrl);

  const curlCreate = `curl -X POST ${host}/public/leads \\
  -H "Authorization: Bearer cwk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "customerName": "Anjali Mehra",
    "mobileNumber": "9812345678",
    "source": "organic_website",
    "spaceType": "private_cabin",
    "seatRangeMin": 4,
    "seatRangeMax": 6,
    "urgency": "high",
    "location": "Indiranagar",
    "spaceId": 1,
    "campaignId": "spring-2026"
  }'`;

  const curlUpdate = `curl -X PATCH ${host}/public/leads/42 \\
  -H "Authorization: Bearer cwk_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "numberVerified": true, "stage": "contacted" }'`;

  const curlUpsert = `curl -X PATCH ${host}/public/leads/lp-spring2026-9812 \\
  -H "Authorization: Bearer cwk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "externalId": "lp-spring2026-9812",
    "customerName": "Anjali Mehra",
    "mobileNumber": "9812345678",
    "source": "meta_ads",
    "spaceType": "private_cabin",
    "seatRangeMin": 4,
    "seatRangeMax": 6,
    "urgency": "high",
    "location": "Indiranagar",
    "spaceId": 1,
    "numberVerified": true,
    "assignedToWhatsappNumber": "9000000002"
  }'`;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-7 w-7" /> Public Lead API
          </h1>
          <p className="text-muted-foreground mt-1">
            Push leads into Coworking Hub from landing pages, ad campaigns, or partner systems.
          </p>
        </div>
        <Link
          href="/api-keys"
          className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
        >
          Get an API Key
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Base URL</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Pre-filled with this environment. Edit to switch hosts when copying examples.
          </p>
          <input
            value={host}
            onChange={(e) => setHost(e.target.value)}
            className="w-full font-mono text-sm border rounded-md px-3 py-2 bg-background"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Authentication</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Every request needs a <code className="bg-muted px-1 rounded">Bearer</code> token. Mint one on the
            {" "}<Link href="/api-keys" className="underline">API Keys</Link> page — the plaintext key is shown
            <strong> once</strong> at creation. Treat it like a password.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Admins and agents</strong> can mint, view, and revoke their own keys.</li>
            <li><strong>Super admins</strong> can additionally mint keys on behalf of any user (by passing <code className="bg-muted px-1 rounded">userId</code>).</li>
          </ul>
          <CodeBlock code={`Authorization: Bearer cwk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`} />
          <p className="text-muted-foreground">
            Keys are stored as SHA-256 hashes server-side; the plaintext is never recoverable. Use <strong>Revoke</strong> in the UI to invalidate a key immediately.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rate Limit</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          60 requests per minute, per API key. Exceeding the limit returns <code className="bg-muted px-1 rounded">429 Too Many Requests</code>.
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-600 hover:bg-emerald-600">POST</Badge>
            <CardTitle className="font-mono text-base">/public/leads</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">Create a new lead.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2 text-sm">Request body</h4>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Field</TableHead>
                    <TableHead className="w-[120px]">Type</TableHead>
                    <TableHead className="w-[80px]">Required</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {createFields.map((f) => (
                    <TableRow key={f.name}>
                      <TableCell className="font-mono text-xs">{f.name}</TableCell>
                      <TableCell className="text-xs">{f.type}</TableCell>
                      <TableCell>
                        {f.required === "yes"
                          ? <Badge variant="destructive" className="text-[10px]">required</Badge>
                          : <Badge variant="outline" className="text-[10px]">optional</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{f.notes ?? ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-2 text-sm">Example</h4>
            <CodeBlock code={curlCreate} />
          </div>

          <div>
            <h4 className="font-semibold mb-2 text-sm">Response 201</h4>
            <CodeBlock code={`{ "id": 42, "externalId": null, "priorityScore": 73, "priority": "high" }`} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Badge className="bg-amber-600 hover:bg-amber-600">PATCH</Badge>
            <CardTitle className="font-mono text-base">/public/leads/{`{idOrExternalId}`}</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">Update an existing lead, or upsert by externalId.</p>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p>
            The path parameter accepts <strong>either</strong> the numeric internal <code className="bg-muted px-1 rounded">id</code> or
            the <code className="bg-muted px-1 rounded">externalId</code> you supplied at create time. If neither matches and the
            body contains all required-on-create fields, the endpoint <strong>upserts</strong> a new lead with that <code className="bg-muted px-1 rounded">externalId</code>
            {" "}— making landing-page integrations safely idempotent on retries.
          </p>
          <div>
            <h4 className="font-semibold mb-2">Update by internal id</h4>
            <CodeBlock code={curlUpdate} />
          </div>
          <div>
            <h4 className="font-semibold mb-2">Idempotent upsert by externalId</h4>
            <CodeBlock code={curlUpsert} />
          </div>
          <div>
            <h4 className="font-semibold mb-2">Response 200</h4>
            <CodeBlock code={`{ "id": 42, "externalId": "lp-spring2026-9812", "priorityScore": 73, "priority": "high", "created": false }`} />
            <p className="text-xs text-muted-foreground mt-1">
              <code className="bg-muted px-1 rounded">created: true</code> indicates a new lead was upserted via externalId.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Errors</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Code</TableHead>
                  <TableHead>Meaning</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow><TableCell className="font-mono">400</TableCell><TableCell>Invalid body or unknown spaceId</TableCell></TableRow>
                <TableRow><TableCell className="font-mono">401</TableCell><TableCell>Missing or invalid bearer token</TableCell></TableRow>
                <TableRow><TableCell className="font-mono">404</TableCell><TableCell>Lead not found (numeric id PATCH only)</TableCell></TableRow>
                <TableRow><TableCell className="font-mono">409</TableCell><TableCell>externalId already exists (POST only)</TableCell></TableRow>
                <TableRow><TableCell className="font-mono">429</TableCell><TableCell>Rate limit exceeded</TableCell></TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Audit Trail &amp; Scoring</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            Every public-API write is recorded as an activity entry on the lead with the actor
            {" "}<code className="bg-muted px-1 rounded">api-key:&lt;prefix&gt;</code> and the key's label, so you can trace which integration created or modified a lead.
          </p>
          <p>
            The lead's <code className="bg-muted px-1 rounded">priorityScore</code> and <code className="bg-muted px-1 rounded">priority</code> are computed automatically from the
            {" "}<Link href="/score-builder" className="underline">Lead Score Builder</Link> rules at write time. To recompute scores after rule changes, super admins can use the <strong>Recalculate All Leads</strong> button.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
